import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Star, X, ImageOff, Search } from "lucide-react";

/**
 * Visual Image Manager for hardware catalog photos.
 * - Lists all hardware with current primary image + gallery thumbnails
 * - Admin clicks a product → modal with pool grid to pick/swap primary + gallery
 * - Persists via PATCH /api/admin/hardware/:id/images (seed no longer overwrites)
 */
export default function ImageManager() {
  const [hardware, setHardware] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorFilter, setVendorFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [pool, setPool] = useState([]);
  const [poolFolder, setPoolFolder] = useState("ik");

  const load = async () => {
    const hw = await api.get("/admin/hardware").then((r) => r.data);
    setHardware(hw);
    const vs = await api.get("/admin/vendors").then((r) => r.data);
    setVendors(vs);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!editing) return;
    api.get(`/admin/image-pool?folder=${encodeURIComponent(poolFolder)}`)
      .then((r) => setPool(r.data.files || []))
      .catch(() => setPool([]));
  }, [editing, poolFolder]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return hardware
      .filter((h) => vendorFilter === "all" || h.vendor_slug === vendorFilter)
      .filter((h) => !q || h.sku.toLowerCase().includes(q) || (h.name || "").toLowerCase().includes(q));
  }, [hardware, vendorFilter, search]);

  return (
    <div data-testid="admin-image-manager">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="display text-3xl font-semibold">Foto Manager</h2>
          <p className="text-sm text-[#A8B3AD] mt-1 max-w-2xl">
            Controleer visueel of elke kiosk de juiste foto heeft. Klik op een product om uit de
            beschikbare pool een nieuwe hoofdfoto of galerij-foto te kiezen. Wijzigingen blijven
            bewaard, ze worden niet meer overschreven bij server-herstart.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="flex items-center gap-2 card-surface px-3 py-2">
          <Search className="w-4 h-4 text-[#6FAA8A]" />
          <input
            className="bg-transparent outline-none text-sm w-64"
            placeholder="Zoek op SKU of naam…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="im-search-input"
          />
        </div>
        <select
          className="input-field text-sm py-2"
          value={vendorFilter}
          onChange={(e) => setVendorFilter(e.target.value)}
          data-testid="im-vendor-filter"
        >
          <option value="all">Alle leveranciers</option>
          {vendors.map((v) => (
            <option key={v.slug} value={v.slug}>{v.name}</option>
          ))}
        </select>
        <div className="text-xs text-[#A8B3AD]">{filtered.length} producten</div>
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((h) => (
          <ProductImageCard key={h.id} hw={h} onEdit={() => setEditing(h)} />
        ))}
      </div>

      {/* Editor modal */}
      {editing && (
        <ImageEditorModal
          hw={editing}
          pool={pool}
          poolFolder={poolFolder}
          setPoolFolder={setPoolFolder}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setHardware((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            setEditing(null);
            toast.success("Afbeeldingen bijgewerkt");
          }}
        />
      )}
    </div>
  );
}

function ProductImageCard({ hw, onEdit }) {
  const placementLabel = { indoor: "Binnen", outdoor: "Buiten", both: "Binnen + buiten" }[hw.placement] || hw.placement || "—";
  const typeLabel = { counter: "Op balie", freestanding: "Op voet", wall: "Hangend" }[hw.type] || hw.type || "—";
  return (
    <button
      onClick={onEdit}
      className="card-surface p-4 text-left hover:ring-1 hover:ring-[#6FAA8A] transition group"
      data-testid={`im-card-${hw.sku}`}
    >
      <div className="aspect-[4/3] bg-[#0F1816] rounded-md overflow-hidden flex items-center justify-center mb-3 relative">
        {hw.image_url ? (
          <img src={hw.image_url} alt={hw.name} className="w-full h-full object-contain" loading="lazy" />
        ) : (
          <ImageOff className="w-8 h-8 text-[#6FAA8A]/40" />
        )}
        <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider bg-[#0B1311]/80 text-[#6FAA8A] px-2 py-1 rounded">
          {hw.vendor_slug}
        </span>
      </div>
      <div className="text-xs text-[#6FAA8A] font-mono">{hw.sku}</div>
      <div className="text-sm font-semibold text-white mt-0.5 line-clamp-2">{hw.name}</div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#6FAA8A]/30 text-[#6FAA8A]" data-testid={`im-card-placement-${hw.sku}`}>{placementLabel}</span>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#D97742]/40 text-[#D97742]" data-testid={`im-card-type-${hw.sku}`}>{typeLabel}</span>
      </div>
      <div className="text-xs text-[#A8B3AD] mt-2">
        {(hw.gallery || []).length} galerij-foto{(hw.gallery || []).length === 1 ? "" : "'s"}
      </div>
      <div className="mt-3 flex gap-1 overflow-x-auto">
        {(hw.gallery || []).slice(0, 6).map((g, i) => (
          <img key={i} src={g} alt="" className="w-10 h-10 object-cover rounded bg-[#0F1816] flex-shrink-0" loading="lazy" />
        ))}
      </div>
      <div className="mt-3 text-xs font-semibold text-[#D97742] group-hover:underline">
        Foto's & eigenschappen bewerken →
      </div>
    </button>
  );
}

function ImageEditorModal({ hw, pool, poolFolder, setPoolFolder, onClose, onSaved }) {
  const [primary, setPrimary] = useState(hw.image_url || "");
  const [gallery, setGallery] = useState(hw.gallery || []);
  const [placement, setPlacement] = useState(hw.placement || "indoor");
  const [type, setType] = useState(hw.type || "freestanding");
  const [saving, setSaving] = useState(false);

  const toggleGallery = (path) => {
    setGallery((prev) => (prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]));
  };
  const makePrimary = (path) => {
    setPrimary(path);
    // Ensure current primary stays in gallery, and new primary is first
    setGallery((prev) => {
      const withoutDup = prev.filter((p) => p !== path);
      return [path, ...withoutDup];
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/hardware/${hw.id}/images`, {
        image_url: primary,
        gallery,
        placement,
        type,
      });
      onSaved(data);
    } catch (e) {
      toast.error("Kon niet opslaan: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto" data-testid="im-editor-modal">
      <div className="bg-[#0B1311] border border-[#1F3A2E] rounded-lg max-w-6xl w-[95%] my-8 p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs text-[#6FAA8A] font-mono">{hw.sku} · {hw.vendor_slug}</div>
            <h3 className="display text-2xl font-semibold text-white">{hw.name}</h3>
            {hw.description_i18n?.nl && (
              <p className="text-sm text-[#A8B3AD] mt-1 max-w-2xl">{hw.description_i18n.nl}</p>
            )}
            <div className="flex gap-3 mt-2 text-xs text-[#A8B3AD]">
              <span>Type: <b className="text-white">{hw.type}</b></span>
              <span>Plaatsing: <b className="text-white">{hw.placement}</b></span>
              {hw.specs?.screen && <span>Scherm: <b className="text-white">{hw.specs.screen}</b></span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#1F3A2E]" data-testid="im-close">
            <X className="w-5 h-5 text-[#A8B3AD]" />
          </button>
        </div>

        {/* Properties, placement + type */}
        <div className="grid md:grid-cols-2 gap-5 bg-[#0F1816] rounded-md p-4 border border-[#1F3A2E]">
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#D97742] mb-2">
              Binnen / buiten
            </label>
            <div className="flex gap-2" data-testid="im-placement-group">
              {[
                { v: "indoor", l: "Binnen" },
                { v: "outdoor", l: "Buiten" },
                { v: "both", l: "Beide" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setPlacement(opt.v)}
                  className={`flex-1 text-sm px-3 py-2 rounded border transition ${
                    placement === opt.v
                      ? "bg-[#D97742] text-white border-[#D97742] font-semibold"
                      : "bg-[#0B1311] text-[#A8B3AD] border-[#1F3A2E] hover:border-[#6FAA8A]"
                  }`}
                  data-testid={`im-placement-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            <div className="text-xs text-[#A8B3AD] mt-2">Bepaalt of het apparaat geschikt is voor binnen, buiten of beide. Beïnvloedt filter-resultaten in de configurator.</div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#D97742] mb-2">
              Opstelling
            </label>
            <div className="flex gap-2" data-testid="im-type-group">
              {[
                { v: "freestanding", l: "Op voet" },
                { v: "counter",      l: "Op balie" },
                { v: "wall",         l: "Hangend" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setType(opt.v)}
                  className={`flex-1 text-sm px-3 py-2 rounded border transition ${
                    type === opt.v
                      ? "bg-[#6FAA8A] text-[#0B1311] border-[#6FAA8A] font-semibold"
                      : "bg-[#0B1311] text-[#A8B3AD] border-[#1F3A2E] hover:border-[#6FAA8A]"
                  }`}
                  data-testid={`im-type-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            <div className="text-xs text-[#A8B3AD] mt-2">Hoe het apparaat fysiek wordt geplaatst. Staand-op-voet, op een balie, of aan de wand gehangen.</div>
          </div>
        </div>

        {/* Current selection */}
        <div className="grid md:grid-cols-2 gap-5 bg-[#0F1816] rounded-md p-4 border border-[#1F3A2E]">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#D97742] mb-2">Huidige hoofdfoto</div>
            <div className="aspect-[4/3] bg-[#0B1311] rounded overflow-hidden flex items-center justify-center">
              {primary ? (
                <img src={primary} alt="" className="w-full h-full object-contain" />
              ) : (
                <div className="text-[#A8B3AD] text-sm">Geen hoofdfoto gekozen</div>
              )}
            </div>
            <div className="text-xs text-[#6FAA8A] mt-2 font-mono break-all">{primary || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#D97742] mb-2">
              Galerij ({gallery.length})
            </div>
            <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
              {gallery.map((g) => (
                <div key={g} className="relative aspect-square bg-[#0B1311] rounded overflow-hidden group">
                  <img src={g} alt="" className="w-full h-full object-cover" />
                  {g === primary && (
                    <span className="absolute top-1 left-1 bg-[#D97742] text-white text-[9px] px-1 rounded font-semibold">
                      HOOFD
                    </span>
                  )}
                  <button
                    onClick={() => toggleGallery(g)}
                    className="absolute top-1 right-1 bg-black/60 rounded p-0.5 opacity-0 group-hover:opacity-100 transition"
                    title="Uit galerij verwijderen"
                    data-testid={`im-gallery-remove-${g}`}
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
              {gallery.length === 0 && (
                <div className="col-span-4 text-xs text-[#A8B3AD] text-center py-6">Leeg</div>
              )}
            </div>
          </div>
        </div>

        {/* Image pool */}
        <div>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#D97742]">
              Beschikbare afbeeldingen
            </div>
            <select
              className="input-field text-xs py-1"
              value={poolFolder}
              onChange={(e) => setPoolFolder(e.target.value)}
              data-testid="im-pool-folder"
            >
              <option value="">Alle mappen</option>
              <option value="ik">Interact Kiosk (ik/)</option>
              <option value="ik/clean">IK · clean PNGs</option>
              <option value="ik/real">IK · real install photos</option>
              <option value="qx">QIOX (qx/)</option>
              <option value="qiox">QIOX (qiox/)</option>
            </select>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-[420px] overflow-y-auto">
            {pool.map((f) => {
              const inGallery = gallery.includes(f.path);
              const isPrimary = primary === f.path;
              return (
                <div key={f.path} className={`relative bg-[#0F1816] rounded overflow-hidden border ${isPrimary ? "border-[#D97742]" : inGallery ? "border-[#6FAA8A]" : "border-transparent"}`}>
                  <div className="aspect-square flex items-center justify-center">
                    <img src={f.path} alt={f.name} className="w-full h-full object-contain" loading="lazy" />
                  </div>
                  <div className="text-[9px] text-[#A8B3AD] px-1 pb-1 truncate" title={f.path}>{f.name}</div>
                  <div className="absolute inset-x-1 top-1 flex gap-1">
                    <button
                      onClick={() => makePrimary(f.path)}
                      className={`flex-1 text-[9px] font-semibold py-1 rounded ${isPrimary ? "bg-[#D97742] text-white" : "bg-black/60 text-white hover:bg-[#D97742]"}`}
                      data-testid={`im-pool-primary-${f.name}`}
                      title="Maak hoofdfoto"
                    >
                      <Star className="w-3 h-3 inline" />
                    </button>
                    <button
                      onClick={() => toggleGallery(f.path)}
                      className={`flex-1 text-[9px] font-semibold py-1 rounded ${inGallery ? "bg-[#6FAA8A] text-[#0B1311]" : "bg-black/60 text-white hover:bg-[#6FAA8A]/80"}`}
                      data-testid={`im-pool-gallery-${f.name}`}
                      title={inGallery ? "Uit galerij" : "In galerij"}
                    >
                      <Check className="w-3 h-3 inline" />
                    </button>
                  </div>
                </div>
              );
            })}
            {pool.length === 0 && (
              <div className="col-span-full text-center text-sm text-[#A8B3AD] py-8">Geen afbeeldingen in deze map.</div>
            )}
          </div>
          <div className="text-[11px] text-[#A8B3AD] mt-2">
            <Star className="w-3 h-3 inline text-[#D97742]" /> = hoofdfoto ·{" "}
            <Check className="w-3 h-3 inline text-[#6FAA8A]" /> = in galerij
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-[#1F3A2E] pt-4">
          <button onClick={onClose} className="btn-ghost" data-testid="im-cancel">Annuleren</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="im-save">
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}
